import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';

export default function SafetyPage() {
  return (
    <>

      <script type="application/ld+json">
        {JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'Article',
          headline: 'Superbuy Spreadsheet Safety Guide 2026 - Avoid Fake Sources & Shop Smarter',
          description: 'A comprehensive safety guide covering everything you need to know about using Superbuy spreadsheets securely, including scam prevention, privacy protection, and payment safety.',
          image: 'https://readdy.ai/api/search-image?query=Minimalist%20security%20concept%20with%20a%20single%20transparent%20glass%20shield%20resting%20on%20a%20warm%20wooden%20desk%20near%20a%20bright%20window%2C%20soft%20morning%20light%20creating%20prismatic%20reflections%2C%20clean%20modern%20workspace%20aesthetic%2C%20calm%20reassuring%20atmosphere%20with%20cream%20and%20oak%20tones&width=1800&height=700&seq=safety-hero-shield-v2&orientation=landscape',
          author: { '@type': 'Organization', name: 'Superbuy Spread Editorial Team' },
          datePublished: '2026-07-12',
          dateModified: '2026-07-12',
          publisher: { '@type': 'Organization', name: 'Superbuy Spread' },
        })}
      </script>
      <script type="application/ld+json">
        {JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'FAQPage',
          mainEntity: [
            {
              '@type': 'Question',
              name: 'Is Superbuy Spreadsheet safe to use?',
              acceptedAnswer: {
                '@type': 'Answer',
                text: 'Yes, Superbuy spreadsheets are safe organizational tools when sourced from reputable platforms. The spreadsheet format itself carries no security risk. However, shoppers should always verify product links, avoid sharing payment information through spreadsheets, and use trusted, regularly updated sources.',
              },
            },
            {
              '@type': 'Question',
              name: 'How can I tell if a Superbuy Spreadsheet is fake?',
              acceptedAnswer: {
                '@type': 'Answer',
                text: 'Fake Superbuy spreadsheets often share common red flags: outdated or broken product links, missing QC photos, requests for personal payment information, no visible update dates, generic or stolen product images, and sellers pushing for direct off-platform transactions. Authentic spreadsheets from trusted platforms like Superbuy Spread are regularly updated and transparent about data sources.',
              },
            },
            {
              '@type': 'Question',
              name: 'What payment methods are safe when using Superbuy Spreadsheet?',
              acceptedAnswer: {
                '@type': 'Answer',
                text: 'Always use secure, traceable payment methods such as credit cards through official checkout systems, verified third-party payment processors, or platform-protected payment gateways. Never send money through direct bank transfers, cryptocurrency, or peer-to-peer payment apps to sellers found through spreadsheets unless you have independently verified their legitimacy.',
              },
            },
            {
              '@type': 'Question',
              name: 'Should I share personal information through Superbuy Spreadsheet platforms?',
              acceptedAnswer: {
                '@type': 'Answer',
                text: 'No. You should never share sensitive personal information — including your full address, ID documents, bank account details, or passwords — through any Superbuy spreadsheet platform or with any seller you discover through a spreadsheet. Legitimate shopping does not require this information to be shared outside of the official checkout process.',
              },
            },
          ],
        })}
      </script>
      <Navbar />
      <main>
        <SafetyHero />
        <SafetyBody />
        <SafetyCTA />
      </main>
      <Footer />
    </>
  );
}

function SafetyHero() {
  return (
    <section className="relative w-full min-h-[450px] md:min-h-[500px] flex items-center overflow-hidden">
      <div className="absolute inset-0">
        <img
          src="https://readdy.ai/api/search-image?query=Minimalist%20security%20concept%20with%20a%20single%20transparent%20glass%20shield%20resting%20on%20a%20warm%20wooden%20desk%20near%20a%20bright%20window%2C%20soft%20morning%20light%20creating%20prismatic%20reflections%2C%20clean%20modern%20workspace%20aesthetic%2C%20calm%20reassuring%20atmosphere%20with%20cream%20and%20oak%20tones&width=1800&height=700&seq=safety-hero-shield-v2&orientation=landscape"
          alt="Superbuy Spreadsheet safety and security guide"
          className="w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-black/20 to-black/40"></div>
      </div>
      <div className="relative z-10 w-full px-6 md:px-10 py-20">
        <div className="max-w-[900px] mx-auto">
          <div className="flex items-center gap-2 text-background-50/70 text-sm mb-4">
            <Link to="/" className="hover:text-background-50 transition-colors">Home</Link>
            <i className="ri-arrow-right-s-line text-xs"></i>
            <span className="text-background-50/50">Safety Guide</span>
          </div>
          <h1 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-background-50 leading-tight mb-4">
            Superbuy Spreadsheet Safety Guide: Avoid Fake Sources &amp; Shop Smarter in 2026
          </h1>
          <p className="text-base md:text-lg text-background-50/80 max-w-2xl leading-relaxed mb-6">
            Your comprehensive resource for staying safe while using Superbuy spreadsheets. Learn to spot scams, protect your data, and shop with total confidence.
          </p>
          <div className="flex flex-wrap items-center gap-4 text-sm text-background-50/60">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-full bg-accent-400/30 flex items-center justify-center">
                <i className="ri-shield-check-line text-accent-300 text-sm"></i>
              </div>
              <span>Superbuy Spread Safety Team</span>
            </div>
            <span className="hidden sm:inline">|</span>
            <span>Published: July 2026</span>
            <span className="hidden sm:inline">|</span>
            <span>10 min read</span>
          </div>
        </div>
      </div>
    </section>
  );
}

function SafetyBody() {
  return (
    <section className="py-12 md:py-20 px-6 md:px-10 bg-background-100">
      <div className="max-w-[800px] mx-auto">
        <article className="bg-background-50 rounded-2xl border border-background-200/50 p-6 md:p-12">
          <div className="flex items-center gap-3 mb-10 pb-6 border-b border-background-200/50">
            <span className="text-xs font-semibold uppercase tracking-widest text-accent-700 bg-accent-100/70 px-3 py-1 rounded-full">
              / safety-guide
            </span>
          </div>

          <div className="prose-custom space-y-7">
            <h2 className="font-heading text-2xl md:text-3xl font-semibold text-foreground-950">
              Understanding Superbuy Spreadsheet Safety: The Essentials
            </h2>
            <p className="text-base text-foreground-700 leading-relaxed">
              Safety should always be your top priority when exploring any <strong>Superbuy spreadsheet</strong>. While these organized product databases are fundamentally safe tools — nothing more than structured collections of links and information — the ecosystem around them can sometimes attract bad actors looking to exploit inexperienced shoppers. This comprehensive guide covers everything you need to know about staying secure while using <strong>Superbuy spreadsheets</strong> in 2026.
            </p>
            <p className="text-base text-foreground-700 leading-relaxed">
              The reality is that a <strong>Superbuy spreadsheet</strong> itself cannot harm your device or steal your information. Spreadsheets are static files that display organized data — they do not execute code, install software, or access your personal files. The actual risks come from two sources: clicking on unverified links to fraudulent stores, and sharing sensitive information with untrustworthy sellers you may discover through a spreadsheet. Understanding this distinction is the first step toward safe shopping. For more on how these tools work, check our{' '}
              <Link to="/superbuy-spreadsheet-guide" className="text-primary-500 underline hover:text-primary-600 transition-colors">
                beginner guide to Superbuy spreadsheets
              </Link>.
            </p>

            <h2 className="font-heading text-2xl md:text-3xl font-semibold text-foreground-950 mt-12">
              Common Safety Risks When Using Superbuy Spreadsheets
            </h2>
            <p className="text-base text-foreground-700 leading-relaxed">
              Before diving into protective strategies, it helps to understand exactly what risks exist. Being aware of these common pitfalls is half the battle when it comes to staying safe with any <strong>Superbuy spreadsheet</strong>.
            </p>

            <h3 className="font-heading text-xl font-semibold text-foreground-950 mt-8">
              Fake or Cloned Superbuy Spreadsheets
            </h3>
            <p className="text-base text-foreground-700 leading-relaxed">
              One of the most prevalent issues in 2026 is the circulation of counterfeit <strong>Superbuy spreadsheets</strong>. Scammers create convincing copies of popular spreadsheets but replace the legitimate product links with links to their own fraudulent storefronts. These fake stores often look professional, complete with stolen product images and fabricated reviews. Unsuspecting shoppers who place orders through these cloned sites may never receive their products — or worse, may have their payment information stolen.
            </p>
            <p className="text-base text-foreground-700 leading-relaxed">
              Always verify that your <strong>Superbuy spreadsheet</strong> comes from a trusted source. Our platform at{' '}
              <Link to="/" className="text-primary-500 underline hover:text-primary-600 transition-colors">Superbuy Spread</Link> maintains strict verification protocols for all linked resources. If you encounter a spreadsheet from an unfamiliar website or social media channel, approach it with healthy skepticism.
            </p>

            <h3 className="font-heading text-xl font-semibold text-foreground-950 mt-8">
              Phishing Through Spreadsheet Links
            </h3>
            <p className="text-base text-foreground-700 leading-relaxed">
              Some bad actors use <strong>Superbuy spreadsheets</strong> as a delivery mechanism for phishing attacks. They embed links that appear to go to legitimate product pages but actually redirect to login-stealing websites designed to capture your account credentials. These phishing pages often mimic the exact look and feel of real shopping platforms, making them difficult to identify at first glance.
            </p>

            <h3 className="font-heading text-xl font-semibold text-foreground-950 mt-8">
              Direct Payment Scams
            </h3>
            <p className="text-base text-foreground-700 leading-relaxed">
              A particularly dangerous trend involves sellers found through <strong>Superbuy spreadsheets</strong> who pressure buyers into making direct payments outside of protected platform checkout systems. These sellers may offer a small discount for using bank transfers, cryptocurrency, or peer-to-peer payment apps. Once the payment is sent, the seller disappears — and because the transaction happened outside of a protected platform, there is virtually no way to recover the funds.
            </p>

            <div className="bg-red-50/50 border border-red-200/50 rounded-xl p-6 my-8">
              <div className="flex items-start gap-3">
                <div className="w-9 h-9 rounded-lg bg-red-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <i className="ri-alert-line text-red-500 text-lg"></i>
                </div>
                <div>
                  <h4 className="font-semibold text-foreground-950 mb-2">Red Flag Warning Signs</h4>
                  <ul className="space-y-2 text-sm text-foreground-600 leading-relaxed">
                    <li className="flex items-start gap-2">
                      <i className="ri-close-circle-line text-red-400 mt-0.5 flex-shrink-0"></i>
                      <span>Sellers who insist on direct payment outside of protected platforms</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <i className="ri-close-circle-line text-red-400 mt-0.5 flex-shrink-0"></i>
                      <span>Prices that seem unrealistically low — if it looks too good to be true, it almost certainly is</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <i className="ri-close-circle-line text-red-400 mt-0.5 flex-shrink-0"></i>
                      <span>Spreadsheets with no visible update date or contact information</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <i className="ri-close-circle-line text-red-400 mt-0.5 flex-shrink-0"></i>
                      <span>Links that redirect to unfamiliar domains or ask for login credentials</span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>

            <h2 className="font-heading text-2xl md:text-3xl font-semibold text-foreground-950 mt-12">
              How to Verify a Safe and Trustworthy Superbuy Spreadsheet
            </h2>
            <p className="text-base text-foreground-700 leading-relaxed">
              Knowing how to evaluate a <strong>Superbuy spreadsheet</strong> is the most important skill you can develop as a smart shopper. Here is a systematic approach to verifying any spreadsheet before you use it.
            </p>

            <div className="bg-background-100 rounded-xl p-6 md:p-8 my-8 space-y-5">
              <div className="flex gap-4">
                <div className="w-8 h-8 rounded-full bg-accent-500 text-background-50 flex items-center justify-center text-sm font-bold flex-shrink-0">1</div>
                <div>
                  <h4 className="font-semibold text-foreground-950 mb-1">Check the Source Platform</h4>
                  <p className="text-sm text-foreground-600 leading-relaxed">
                    A legitimate <strong>Superbuy spreadsheet</strong> should be hosted on a recognizable platform with a clear domain and professional presentation. Platforms like Superbuy Spread maintain consistent branding, clear navigation, and transparent about pages. Avoid spreadsheets shared through random file-sharing links or unverified social media accounts.
                  </p>
                </div>
              </div>
              <div className="flex gap-4">
                <div className="w-8 h-8 rounded-full bg-accent-500 text-background-50 flex items-center justify-center text-sm font-bold flex-shrink-0">2</div>
                <div>
                  <h4 className="font-semibold text-foreground-950 mb-1">Look for Update Dates</h4>
                  <p className="text-sm text-foreground-600 leading-relaxed">
                    Trustworthy <strong>Superbuy spreadsheets</strong> always display a last-updated date prominently. This transparency shows that the maintainers are actively curating the data. An unmaintained spreadsheet with dead links is not just inconvenient — it is a sign that no one is watching for security issues either.
                  </p>
                </div>
              </div>
              <div className="flex gap-4">
                <div className="w-8 h-8 rounded-full bg-accent-500 text-background-50 flex items-center justify-center text-sm font-bold flex-shrink-0">3</div>
                <div>
                  <h4 className="font-semibold text-foreground-950 mb-1">Verify QC Photo Authenticity</h4>
                  <p className="text-sm text-foreground-600 leading-relaxed">
                    Real <strong>Superbuy spreadsheets</strong> include genuine QC (quality control) photos taken by actual buyers. These images typically show products in natural lighting with visible details and sometimes minor imperfections — not the polished studio shots found on official product pages. If every image looks like a professional catalog photo, be suspicious.
                  </p>
                </div>
              </div>
              <div className="flex gap-4">
                <div className="w-8 h-8 rounded-full bg-accent-500 text-background-50 flex items-center justify-center text-sm font-bold flex-shrink-0">4</div>
                <div>
                  <h4 className="font-semibold text-foreground-950 mb-1">Cross-Reference Product Links</h4>
                  <p className="text-sm text-foreground-600 leading-relaxed">
                    Before clicking any link in a <strong>Superbuy spreadsheet</strong>, hover over it to preview the destination URL. The domain should match the expected shopping platform. If a product link redirects through multiple unfamiliar domains or uses URL shorteners, treat it as highly suspicious and do not click.
                  </p>
                </div>
              </div>
            </div>

            <h2 className="font-heading text-2xl md:text-3xl font-semibold text-foreground-950 mt-12">
              Protecting Your Personal Information
            </h2>
            <p className="text-base text-foreground-700 leading-relaxed">
              Information security goes hand in hand with <strong>Superbuy spreadsheet</strong> safety. The data you share while shopping can be just as valuable to scammers as your payment details. Here is what you should never share through any spreadsheet-related platform or communication channel.
            </p>

            <div className="bg-accent-50/50 border border-accent-200/50 rounded-xl p-6 my-8">
              <div className="flex items-start gap-3">
                <div className="w-9 h-9 rounded-lg bg-accent-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <i className="ri-lock-line text-accent-600 text-lg"></i>
                </div>
                <div>
                  <h4 className="font-semibold text-foreground-950 mb-2">Personal Data Protection Checklist</h4>
                  <ul className="space-y-2 text-sm text-foreground-600 leading-relaxed">
                    <li className="flex items-start gap-2">
                      <i className="ri-check-line text-accent-500 mt-0.5 flex-shrink-0"></i>
                      <span><strong>Never share your full home address</strong> outside of the official checkout page on the shopping platform</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <i className="ri-check-line text-accent-500 mt-0.5 flex-shrink-0"></i>
                      <span><strong>Never send photos of your ID</strong> or passport to sellers — legitimate purchases do not require this</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <i className="ri-check-line text-accent-500 mt-0.5 flex-shrink-0"></i>
                      <span><strong>Never share banking login credentials</strong> or one-time verification codes with anyone</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <i className="ri-check-line text-accent-500 mt-0.5 flex-shrink-0"></i>
                      <span><strong>Use a dedicated email address</strong> for shopping to isolate potential spam from your primary inbox</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <i className="ri-check-line text-accent-500 mt-0.5 flex-shrink-0"></i>
                      <span><strong>Enable two-factor authentication</strong> on all shopping platform accounts for an extra security layer</span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>

            <h2 className="font-heading text-2xl md:text-3xl font-semibold text-foreground-950 mt-12">
              Secure Payment Methods for Superbuy Spreadsheet Shopping
            </h2>
            <p className="text-base text-foreground-700 leading-relaxed">
              How you pay matters just as much as where you shop. When using a <strong>Superbuy spreadsheet</strong> to discover products, always complete your purchases through secure, protected payment channels that offer buyer protection and fraud monitoring.
            </p>

            <h3 className="font-heading text-xl font-semibold text-foreground-950 mt-8">
              Recommended Payment Methods
            </h3>
            <ul className="space-y-3 text-base text-foreground-700 leading-relaxed">
              <li className="flex items-start gap-2">
                <i className="ri-checkbox-circle-fill text-accent-500 mt-1 flex-shrink-0"></i>
                <span><strong>Credit cards</strong> through official platform checkout — most credit card companies provide robust fraud protection and chargeback rights.</span>
              </li>
              <li className="flex items-start gap-2">
                <i className="ri-checkbox-circle-fill text-accent-500 mt-1 flex-shrink-0"></i>
                <span><strong>Verified payment processors</strong> such as those integrated directly into the shopping platform — these services act as a secure intermediary.</span>
              </li>
              <li className="flex items-start gap-2">
                <i className="ri-checkbox-circle-fill text-accent-500 mt-1 flex-shrink-0"></i>
                <span><strong>Platform-protected payment gateways</strong> that hold funds in escrow until the order is confirmed delivered.</span>
              </li>
            </ul>

            <h3 className="font-heading text-xl font-semibold text-foreground-950 mt-8">
              Payment Methods to Avoid
            </h3>
            <ul className="space-y-3 text-base text-foreground-700 leading-relaxed">
              <li className="flex items-start gap-2">
                <i className="ri-close-circle-fill text-red-400 mt-1 flex-shrink-0"></i>
                <span><strong>Direct bank transfers</strong> — these offer virtually no recourse if something goes wrong with your order.</span>
              </li>
              <li className="flex items-start gap-2">
                <i className="ri-close-circle-fill text-red-400 mt-1 flex-shrink-0"></i>
                <span><strong>Cryptocurrency payments</strong> — irreversible by design, making them a favorite tool for scammers.</span>
              </li>
              <li className="flex items-start gap-2">
                <i className="ri-close-circle-fill text-red-400 mt-1 flex-shrink-0"></i>
                <span><strong>Peer-to-peer payment apps</strong> without buyer protection — these are designed for sending money to friends, not for purchases from unknown sellers.</span>
              </li>
            </ul>

            <h2 className="font-heading text-2xl md:text-3xl font-semibold text-foreground-950 mt-12">
              How to Report Suspicious Superbuy Spreadsheets
            </h2>
            <p className="text-base text-foreground-700 leading-relaxed">
              If you encounter a <strong>Superbuy spreadsheet</strong> that appears suspicious or fraudulent, reporting it helps protect the entire community. Document what you found — take screenshots of the suspicious links, note the platform where you discovered it, and gather any communication you received about it. Report the issue to the platform hosting the spreadsheet, and share your findings with trusted community forums where other shoppers can benefit from your vigilance.
            </p>
            <p className="text-base text-foreground-700 leading-relaxed">
              At <strong>Superbuy Spread</strong>, we take safety seriously. Our platform at{' '}
              <Link to="/" className="text-primary-500 underline hover:text-primary-600 transition-colors">superbuyspread.xyz</Link> only links to verified, regularly updated resources. We also recommend reviewing our{' '}
              <Link to="/superbuy-spreadsheet-vs-traditional" className="text-primary-500 underline hover:text-primary-600 transition-colors">comparison guide</Link> to understand the advantages of using curated spreadsheets over manual browsing.
            </p>

            <h2 className="font-heading text-2xl md:text-3xl font-semibold text-foreground-950 mt-12">
              Frequently Asked Questions
            </h2>
            <div className="space-y-4 mt-6">
              <FAQItem
                question="Is Superbuy Spreadsheet safe to use?"
                answer="Yes, Superbuy spreadsheets are safe organizational tools when sourced from reputable platforms. The spreadsheet format itself carries no security risk. However, shoppers should always verify product links, avoid sharing payment information through spreadsheets, and use trusted, regularly updated sources like Superbuy Spread."
              />
              <FAQItem
                question="How can I tell if a Superbuy Spreadsheet is fake?"
                answer="Fake Superbuy spreadsheets often share common red flags: outdated or broken product links, missing QC photos, requests for personal payment information, no visible update dates, generic or stolen product images, and sellers pushing for direct off-platform transactions. Authentic spreadsheets from trusted platforms are regularly updated and transparent about data sources."
              />
              <FAQItem
                question="What should I do if I clicked a suspicious link from a Superbuy Spreadsheet?"
                answer="If you clicked a suspicious link, immediately close the page. Do not enter any information. Run a security scan on your device, change your passwords if you entered any credentials, and monitor your financial accounts for unauthorized activity. Report the spreadsheet to the platform where you found it."
              />
              <FAQItem
                question="Are free Superbuy Spreadsheets less safe than paid ones?"
                answer="Not necessarily. The safety of a Superbuy spreadsheet depends on the source and maintenance practices, not the price. Many excellent free spreadsheets are maintained by dedicated community members. However, be especially cautious of spreadsheets that are aggressively promoted as free but pressure you toward specific sellers or payment methods."
              />
            </div>

            <p className="text-sm text-foreground-500 mt-8 pt-6 border-t border-background-200/50">
              Last Updated: July 2026 &bull; Review Date: August 2026 &bull; Written by the Superbuy Spread Safety Team
            </p>
          </div>
        </article>

        <div className="mt-10 text-center">
          <Link to="/" className="inline-flex items-center gap-2 text-sm text-foreground-600 hover:text-primary-500 transition-colors">
            <i className="ri-arrow-left-line"></i>
            Back to Homepage
          </Link>
        </div>
      </div>
    </section>
  );
}

function FAQItem({ question, answer }: { question: string; answer: string }) {
  return (
    <details className="group bg-background-50 rounded-xl border border-background-200/50 overflow-hidden">
      <summary className="flex items-center justify-between px-6 py-4 cursor-pointer hover:bg-background-100/50 transition-colors list-none">
        <span className="text-sm md:text-base font-medium text-foreground-800 pr-4">{question}</span>
        <i className="ri-add-line text-foreground-400 group-open:hidden flex-shrink-0"></i>
        <i className="ri-subtract-line text-foreground-400 hidden group-open:block flex-shrink-0"></i>
      </summary>
      <div className="px-6 pb-4">
        <p className="text-sm text-foreground-600 leading-relaxed">{answer}</p>
      </div>
    </details>
  );
}

function SafetyCTA() {
  return (
    <section className="py-16 md:py-24 px-6 md:px-10 bg-background-50">
      <div className="max-w-[800px] mx-auto text-center">
        <div className="relative rounded-2xl overflow-hidden mb-10">
          <img
            src="https://readdy.ai/api/search-image?query=Confident%20shopper%20browsing%20on%20tablet%20in%20bright%20modern%20living%20room%20with%20warm%20natural%20light%2C%20minimal%20Scandinavian%20interior%20with%20plants%20and%20neutral%20tones%2C%20calm%20secure%20atmosphere%2C%20lifestyle%20editorial%20photography&width=1000&height=400&seq=safety-cta-confident-v2&orientation=landscape"
            alt="Shop safely with Superbuy Spreadsheet verified resources"
            className="w-full h-56 md:h-72 object-cover"
          />
        </div>
        <h2 className="font-heading text-3xl md:text-4xl font-bold text-foreground-950 mb-4 leading-tight">
          Stay Safe While You{' '}
          <span className="italic font-normal text-primary-500">Shop Smarter</span>
        </h2>
        <p className="text-base text-foreground-600 max-w-lg mx-auto mb-8 leading-relaxed">
          Explore our verified Superbuy spreadsheet categories with confidence. Every resource is reviewed and updated regularly.
        </p>
        <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
          <Link to="/" className="inline-flex items-center gap-2 px-7 py-3.5 bg-foreground-950 text-background-50 text-sm font-semibold rounded-full hover:bg-foreground-800 transition-all duration-300 whitespace-nowrap cursor-pointer">
            Browse Categories
            <i className="ri-arrow-right-line text-base"></i>
          </Link>
          <a href="https://kaspreadsheet.com" target="_blank" rel="noopener nofollow" className="inline-flex items-center gap-2 px-7 py-3.5 bg-primary-500 text-background-50 text-sm font-semibold rounded-full hover:bg-primary-600 transition-all duration-300 whitespace-nowrap cursor-pointer">
            Visit Main Website
            <i className="ri-arrow-right-up-line text-base"></i>
          </a>
        </div>
      </div>
    </section>
  );
}